import React, { useState } from 'react'
import axios from 'axios'

const API  = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const TEAL      = '#0d9488'
const TEAL_DARK = '#0f766e'
const TEAL_LIGHT= '#f0fdfa'
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

//  Step definitions 
const STEPS = [
  { id: 'welcome',    title: 'Welcome to ClearPath',    icon: '', desc: 'Let\'s get your clinic set up in about 5 minutes.' },
  { id: 'clinic',     title: 'Your Clinic',              icon: '', desc: 'Tell us about your practice.' },
  { id: 'contact',    title: 'Your Info',                icon: '', desc: 'How your team and patients will reach you.' },
  { id: 'first_patient', title: 'Add Your First Patient', icon: '', desc: 'Optional — you can always add patients later.' },
  { id: 'done',       title: 'You\'re All Set!',         icon: '', desc: 'Your clinic is ready to go.' },
]

//  Sub-components 
function ProgressBar({ step, total }) {
  return (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '32px' }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          height: '4px', flex: 1, borderRadius: '99px', transition: 'background 0.35s',
          background: i < step ? TEAL : i === step ? `${TEAL}55` : '#e2e8f0',
        }}/>
      ))}
      <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', marginLeft: '4px', flexShrink: 0 }}>
        {step + 1}/{total}
      </div>
    </div>
  )
}

function Label({ children, required }) {
  return (
    <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'flex', gap: '4px' }}>
      {children}
      {required && <span style={{ color: TEAL }}>*</span>}
    </div>
  )
}

function Input({ value, onChange, placeholder, type = 'text', disabled }) {
  const [focused, setFocused] = useState(false)
  return (
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} disabled={disabled}
      style={{
        width: '100%', padding: '10px 14px', fontSize: '14px',
        border: `1.5px solid ${focused ? TEAL : '#e2e8f0'}`,
        borderRadius: '10px', outline: 'none', fontFamily: FONT,
        background: disabled ? '#f8fafc' : 'white',
        color: '#0f172a', transition: 'border-color 0.15s',
        boxSizing: 'border-box',
      }}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
    />
  )
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value} onChange={e => onChange(e.target.value)}
      style={{
        width: '100%', padding: '10px 14px', fontSize: '14px',
        border: '1.5px solid #e2e8f0', borderRadius: '10px', outline: 'none',
        fontFamily: FONT, background: 'white', color: '#0f172a', cursor: 'pointer',
        boxSizing: 'border-box', appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2394a3b8' strokeWidth='1.5' fill='none' strokeLinecap='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center',
        paddingRight: '36px',
      }}
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  )
}

function CheckRow({ checked, onChange, label, sub }) {
  return (
    <button onClick={() => onChange(!checked)} style={{
      display: 'flex', alignItems: 'center', gap: '12px', width: '100%',
      padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
      border: `1.5px solid ${checked ? TEAL : '#e2e8f0'}`,
      background: checked ? TEAL_LIGHT : 'white',
      transition: 'all 0.13s', textAlign: 'left', fontFamily: FONT,
    }}>
      <div style={{
        width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0,
        border: `2px solid ${checked ? TEAL : '#cbd5e1'}`,
        background: checked ? TEAL : 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.13s',
      }}>
        {checked && (
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
            <polyline points="2,6 5,9 10,3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <div>
        <div style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>{label}</div>
        {sub && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '1px' }}>{sub}</div>}
      </div>
    </button>
  )
}

//  Main wizard 
export default function SetupWizard({ onComplete, currentUser }) {
  const [step,    setStep]    = useState(0)
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')

  // Form data
  const [clinic, setClinic] = useState({
    name: '', type: 'outpatient', size: 'solo', phone: '', address: '',
  })
  const [contact, setContact] = useState({
    title: '', specialty: '', notifications_email: true, notifications_sms: false,
  })
  const [patient, setPatient] = useState({
    skip: false, name: '', phone: '', injury: '',
  })

  const canNext = () => {
    if (step === 1) return clinic.name.trim().length > 0
    if (step === 2) return true
    return true
  }

  const handleNext = async () => {
    setError('')
    if (step === STEPS.length - 2) {
      // Final step — save data
      setLoading(true)
      try {
        // Save clinic settings
        await axios.post(API + '/settings/clinic', {
          clinic_name: clinic.name,
          clinic_type: clinic.type,
          clinic_phone: clinic.phone,
          clinic_address: clinic.address,
          therapist_title: contact.title,
          specialty: contact.specialty,
          setup_complete: true,
        }).catch(() => {}) // best-effort

        // Add first patient if provided
        if (!patient.skip && patient.name.trim()) {
          await axios.post(API + '/patients', {
            name: patient.name,
            phone: patient.phone,
            injury_type: patient.injury,
          }).catch(() => {})
        }
      } catch {
        // Non-blocking — proceed even if API fails
      } finally {
        setLoading(false)
      }
    }
    setStep(s => Math.min(s + 1, STEPS.length - 1))
  }

  const currentStep = STEPS[step]

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(15,23,42,0.72)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px', fontFamily: FONT,
    }}>
      <div style={{
        width: '100%', maxWidth: '540px', background: 'white',
        borderRadius: '24px', overflow: 'hidden',
        boxShadow: '0 32px 80px rgba(0,0,0,0.28)',
        animation: 'wizardIn 0.35s cubic-bezier(0.34,1.56,0.64,1)',
      }}>

        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${TEAL_DARK} 0%, #065f46 100%)`,
          padding: '24px 28px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ fontSize: '24px' }}>{currentStep.icon}</div>
              <div>
                <div style={{ fontSize: '18px', fontWeight: '800', color: 'white', letterSpacing: '-0.02em' }}>
                  {currentStep.title}
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '1px' }}>
                  {currentStep.desc}
                </div>
              </div>
            </div>
            {/* ClearPath logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: 0.6 }}>
              <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
                <path d="M16 5 C16 5 10 9 10 15 C10 18.3 12.7 21 16 21 C19.3 21 22 18.3 22 15 C22 9 16 5 16 5Z" stroke="white" strokeWidth="1.8" fill="none"/>
                <line x1="16" y1="21" x2="16" y2="24" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
                <line x1="13" y1="23" x2="19" y2="23" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              <span style={{ fontSize: '12px', fontWeight: '700', color: 'white', letterSpacing: '-0.01em' }}>ClearPath PT</span>
            </div>
          </div>
          <ProgressBar step={step} total={STEPS.length} />
        </div>

        {/* Body */}
        <div style={{ padding: '28px', minHeight: '320px', display: 'flex', flexDirection: 'column' }}>

          {/* STEP 0: Welcome */}
          {step === 0 && (
            <div style={{ flex: 1 }}>
              <div style={{ textAlign: 'center', padding: '12px 0 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}></div>
                <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px', lineHeight: 1.2 }}>
                  Welcome to ClearPath PT!
                </h2>
                <p style={{ fontSize: '15px', color: '#64748b', lineHeight: 1.7, maxWidth: '380px', margin: '0 auto' }}>
                  You're just a few steps away from a fully set up practice management system.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
                {[
                  { icon: '', label: 'Your clinic info',         sub: 'Name, type, contact details' },
                  { icon: '', label: 'Your therapist profile',   sub: 'Title, specialty, notifications' },
                  { icon: '', label: 'Optional: first patient', sub: 'Get up and running fast' },
                ].map((item, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 16px', borderRadius: '12px',
                    background: '#f8fafc', border: '1px solid #f1f5f9',
                  }}>
                    <div style={{ fontSize: '22px' }}>{item.icon}</div>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{item.label}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{item.sub}</div>
                    </div>
                    <div style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: '700', color: TEAL }}>
                      Step {i + 2}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '12px', color: '#94a3b8' }}>
                ⏱ Takes about 5 minutes · You can edit everything later
              </div>
            </div>
          )}

          {/* STEP 1: Clinic */}
          {step === 1 && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <Label required>Clinic / Practice Name</Label>
                <Input value={clinic.name} onChange={v => setClinic(p => ({ ...p, name: v }))} placeholder="e.g. Birmingham Sports PT" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <Label>Practice Type</Label>
                  <Select value={clinic.type} onChange={v => setClinic(p => ({ ...p, type: v }))}
                    options={[
                      { value: 'outpatient',  label: 'Outpatient PT' },
                      { value: 'sports',      label: 'Sports Medicine' },
                      { value: 'ortho',       label: 'Orthopaedics' },
                      { value: 'neuro',       label: 'Neurological' },
                      { value: 'pediatric',   label: 'Pediatric' },
                      { value: 'geriatric',   label: 'Geriatric' },
                      { value: 'home_health', label: 'Home Health' },
                      { value: 'other',       label: 'Other' },
                    ]}
                  />
                </div>
                <div>
                  <Label>Practice Size</Label>
                  <Select value={clinic.size} onChange={v => setClinic(p => ({ ...p, size: v }))}
                    options={[
                      { value: 'solo',   label: 'Solo (just me)' },
                      { value: 'small',  label: 'Small (2-5 staff)' },
                      { value: 'medium', label: 'Medium (6-15)' },
                      { value: 'large',  label: 'Large (16+)' },
                    ]}
                  />
                </div>
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input value={clinic.phone} onChange={v => setClinic(p => ({ ...p, phone: v }))} placeholder="(555) 000-0000" type="tel" />
              </div>
              <div>
                <Label>Address (optional)</Label>
                <Input value={clinic.address} onChange={v => setClinic(p => ({ ...p, address: v }))} placeholder="123 Main St, City, State 00000" />
              </div>
            </div>
          )}

          {/* STEP 2: Contact / therapist profile */}
          {step === 2 && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <Label>Your Title</Label>
                  <Select value={contact.title} onChange={v => setContact(p => ({ ...p, title: v }))}
                    options={[
                      { value: 'DPT',   label: 'DPT — Doctor of PT' },
                      { value: 'PT',    label: 'PT — Physical Therapist' },
                      { value: 'PTA',   label: 'PTA — PT Assistant' },
                      { value: 'OT',    label: 'OT — Occupational Therapist' },
                      { value: 'COTA',  label: 'COTA — OT Assistant' },
                      { value: 'ATC',   label: 'ATC — Athletic Trainer' },
                      { value: 'admin', label: 'Clinic Admin / Owner' },
                      { value: 'other', label: 'Other' },
                    ]}
                  />
                </div>
                <div>
                  <Label>Primary Specialty</Label>
                  <Select value={contact.specialty} onChange={v => setContact(p => ({ ...p, specialty: v }))}
                    options={[
                      { value: '',            label: 'General / All-round' },
                      { value: 'sports',      label: 'Sports & Athletic' },
                      { value: 'ortho',       label: 'Orthopaedic' },
                      { value: 'spine',       label: 'Spine & Back' },
                      { value: 'neuro',       label: 'Neurological' },
                      { value: 'peds',        label: 'Pediatric' },
                      { value: 'geriatric',   label: 'Geriatric' },
                      { value: 'cardio',      label: 'Cardiopulmonary' },
                      { value: 'womens',      label: "Women's Health" },
                      { value: 'handtherapy', label: 'Hand Therapy' },
                    ]}
                  />
                </div>
              </div>

              <div>
                <Label>Notification Preferences</Label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <CheckRow
                    checked={contact.notifications_email}
                    onChange={v => setContact(p => ({ ...p, notifications_email: v }))}
                    label="Email notifications"
                    sub="Patient messages, appointment requests, alerts"
                  />
                  <CheckRow
                    checked={contact.notifications_sms}
                    onChange={v => setContact(p => ({ ...p, notifications_sms: v }))}
                    label="SMS / text notifications"
                    sub="Urgent alerts and appointment reminders"
                  />
                </div>
              </div>

              <div style={{ padding: '12px 16px', borderRadius: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#166534', marginBottom: '3px' }}> HIPAA Compliant</div>
                <div style={{ fontSize: '11px', color: '#166534', opacity: 0.8 }}>
                  All notifications are sent through encrypted, HIPAA-compliant channels. No PHI is ever sent via unsecured SMS.
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: First patient (optional) */}
          {step === 3 && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <CheckRow
                checked={patient.skip}
                onChange={v => setPatient(p => ({ ...p, skip: v }))}
                label="Skip for now — I'll add patients later"
                sub="You can add patients from the dashboard at any time"
              />

              {!patient.skip && (
                <>
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '14px' }}>
                    <Label>Patient Name</Label>
                    <Input value={patient.name} onChange={v => setPatient(p => ({ ...p, name: v }))} placeholder="Full name" />
                  </div>
                  <div>
                    <Label>Patient Phone (optional)</Label>
                    <Input value={patient.phone} onChange={v => setPatient(p => ({ ...p, phone: v }))} placeholder="(555) 000-0000" type="tel" />
                  </div>
                  <div>
                    <Label>Primary Injury / Condition</Label>
                    <Input value={patient.injury} onChange={v => setPatient(p => ({ ...p, injury: v }))} placeholder="e.g. ACL tear, lower back pain" />
                  </div>
                  <div style={{ padding: '10px 14px', borderRadius: '10px', background: TEAL_LIGHT, border: '1px solid #99f6e4', fontSize: '11px', color: '#0f766e' }}>
                     After setup, you can send this patient a digital intake form, track their sessions, assign exercises, and more.
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 4: Done */}
          {step === 4 && (
            <div style={{ flex: 1, textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: '52px', marginBottom: '16px' }}></div>
              <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', marginBottom: '10px', letterSpacing: '-0.02em' }}>
                {clinic.name ? `${clinic.name} is ready!` : 'Your clinic is ready!'}
              </h2>
              <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.7, marginBottom: '20px', maxWidth: '360px', margin: '0 auto 20px' }}>
                Your practice is set up. Here's what you can do next:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '360px', margin: '0 auto' }}>
                {[
                  { icon: '', label: 'Add more patients',         desc: 'From the Patients tab' },
                  { icon: '', label: 'Log your first session',      desc: 'Open any patient and tap + Session' },
                  { icon: '',  label: 'Try Caleb AI',                desc: 'Click the  button anytime' },
                  { icon: '', label: 'Explore the Injury Library',  desc: 'From the left sidebar' },
                ].map((item, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '10px 14px', borderRadius: '10px',
                    background: '#f8fafc', border: '1px solid #f1f5f9', textAlign: 'left',
                  }}>
                    <span style={{ fontSize: '18px' }}>{item.icon}</span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{item.label}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ padding: '10px 14px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', fontSize: '12px', color: '#dc2626' }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 28px 24px', display: 'flex',
          justifyContent: step > 0 ? 'space-between' : 'flex-end',
          alignItems: 'center', borderTop: '1px solid #f1f5f9',
        }}>
          {step > 0 && step < STEPS.length - 1 && (
            <button
              onClick={() => setStep(s => s - 1)}
              style={{
                background: 'none', border: '1.5px solid #e2e8f0', padding: '10px 20px',
                borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#64748b',
                cursor: 'pointer', fontFamily: FONT, transition: 'all 0.13s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#94a3b8'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
            >← Back</button>
          )}

          {step < STEPS.length - 1 ? (
            <button
              onClick={handleNext}
              disabled={!canNext() || loading}
              style={{
                padding: '11px 28px', borderRadius: '10px', fontSize: '14px', fontWeight: '700',
                background: canNext() ? `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})` : '#e2e8f0',
                color: canNext() ? 'white' : '#94a3b8',
                border: 'none', cursor: canNext() ? 'pointer' : 'default',
                fontFamily: FONT, transition: 'all 0.15s',
                boxShadow: canNext() ? '0 4px 16px rgba(13,148,136,0.28)' : 'none',
              }}
            >
              {loading ? 'Saving…' : step === STEPS.length - 2 ? 'Finish Setup ' : 'Continue →'}
            </button>
          ) : (
            <button
              onClick={onComplete}
              style={{
                padding: '12px 32px', borderRadius: '10px', fontSize: '15px', fontWeight: '700',
                background: `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})`,
                color: 'white', border: 'none', cursor: 'pointer', fontFamily: FONT,
                boxShadow: '0 4px 16px rgba(13,148,136,0.35)', width: '100%',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
               Go to my dashboard
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes wizardIn {
          from { opacity: 0; transform: scale(0.94) translateY(16px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
      `}</style>
    </div>
  )
}
